/**
 * ai-digest.js — Scheduled AI Market Digest for SPEKTRX
 *
 * Generates a concise daily market + portfolio briefing via LLM.
 * Data sources: market-intel.js (external APIs) + portfolio state (internal).
 *
 * Storage: sessionStorage only — zero Supabase writes.
 * Auto-triggers on dashboard load if digest is stale (>8h or new day).
 *
 * Exports:
 * - renderDigestCard()   — render or update the digest dashboard card
 * - generateDigest()     — force-generate a new digest (manual refresh)
 * - getLastDigest()      — get cached digest text
 */

import { S } from '../core/state.js';
import { chatCompletion, isAIConfigured } from '../core/ai.js';
import { getMarketIntel } from './market-intel.js';
import { hVal, hTotUSD } from '../core/math.js';
import { esc, toUSD, openTrades, fxUSDTRY, fxEURTRY, promptSafe } from '../core/utils.js';

// ─── CONSTANTS ──────────────────────────────────────────────────

const DIGEST_KEY = 'spektrx_digest';
const STALE_MS = 8 * 60 * 60 * 1000; // 8 hours
let _digestLoading = false;

// ─── CACHE (sessionStorage) ─────────────────────────────────────

function _loadDigest() {
  try {
    const raw = sessionStorage.getItem(DIGEST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
}

function _saveDigest(digest) {
  try {
    sessionStorage.setItem(DIGEST_KEY, JSON.stringify(digest));
  } catch (_e) { /* quota */ }
}

function _isStale() {
  const d = _loadDigest();
  if (!d || !d.ts) return true;
  // Stale if >8h old OR different calendar day
  const age = Date.now() - d.ts;
  if (age > STALE_MS) return true;
  const today = new Date().toISOString().slice(0, 10);
  const digestDay = new Date(d.ts).toISOString().slice(0, 10);
  return today !== digestDay;
}

// ─── DATA GATHERING ─────────────────────────────────────────────

/** Build context string for the LLM digest prompt */
async function _buildDigestContext() {
  const parts = [];
  const fxR = fxUSDTRY();
  if (fxR == null) parts.push('FX: USD/TRY UNAVAILABLE — every TRY figure below is omitted, not converted at parity.');

  // 1. Portfolio summary
  const safeH = (S.holdings || []).filter(h => h && !h._del);
  let totalVal = 0, totalInv = 0;
  safeH.forEach(h => {
    try {
      const v = hVal(h);
      // hVal returns { vL, vU } — there is no iU. `v.iU || 0` made totalInv permanently 0,
      // so the digest prompt claimed P&L equal to the ENTIRE portfolio at 0.0%, and the LLM
      // wrote the dashboard brief on a 100%-gain premise. (audit 2026-08-01)
      if (v.vU) { totalVal += v.vU; totalInv += hTotUSD(h) || 0; }
    } catch (_e) { /* skip */ }
  });
  const pnl = totalVal - totalInv;
  const pnlP = totalInv > 0 ? (pnl / totalInv) * 100 : 0;

  parts.push(`PORTFOLIO: ${safeH.length} holdings, value $${totalVal.toFixed(0)}, P&L ${pnl >= 0 ? '+' : ''}$${pnl.toFixed(0)} (${pnlP.toFixed(1)}%), USD/TRY: ${fxR.toFixed(2)}`);

  // Top movers from holdings
  const movers = safeH.map(h => {
    try {
      const p = S.prices?.[h.ticker];
      return p ? { ticker: h.ticker, chg: p.changePct || 0 } : null;
    } catch (_e) { return null; }
  }).filter(Boolean).sort((a, b) => Math.abs(b.chg) - Math.abs(a.chg)).slice(0, 5);

  if (movers.length) {
    parts.push(`TOP MOVERS: ${movers.map(m => `${m.ticker} ${m.chg >= 0 ? '+' : ''}${m.chg.toFixed(2)}%`).join(', ')}`);
  }

  // Snapshots trend
  // snaps[len-1] vs snaps[len-2] is SNAPSHOTS ago, not days ago. Snapshots are written
  // only on days the app is opened, so "DAILY CHANGE" could report a three-week delta.
  // Also deposit-inclusive: a payday reads as a market gain. Both are now stated, and the
  // window is picked by DATE with the actual span named. (audit 2026-08-01)
  const snaps = (S.snapshots || []).filter(s => s && s.totalWealth > 0 && s.date)
    .slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));
  if (snaps.length >= 2) {
    const latest = snaps[snaps.length - 1];
    const latestMs = Date.parse(latest.date);
    const nearest = targetDays => {
      const want = latestMs - targetDays * 86400000;
      let best = null, bestGap = Infinity;
      for (let i = 0; i < snaps.length - 1; i++) {
        const gap = Math.abs(Date.parse(snaps[i].date) - want);
        if (gap < bestGap) { bestGap = gap; best = snaps[i]; }
      }
      return best;
    };
    const change = (from, label) => {
      if (!from || !(from.totalWealth > 0)) return;
      const days = Math.round((latestMs - Date.parse(from.date)) / 86400000);
      if (!(days > 0)) return;
      const pct = (latest.totalWealth - from.totalWealth) / from.totalWealth * 100;
      parts.push(`${label} (${days}d actual, incl. deposits): ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`);
    };
    change(nearest(1), 'CHANGE SINCE PRIOR SNAPSHOT');
    if (snaps.length >= 3) change(nearest(7), 'WEEKLY CHANGE');
  }

  // Active trades — directional only; deposits get their own line below rather than being
  // counted as trades whose P&L is (price - entry).
  const trades = openTrades(S.trades);
  if (trades.length) {
    let tPnL = 0;
    trades.forEach(t => {
      const p = S.prices?.[t.ticker];
      if (!p) return;
      const pl = (t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * (t.leverage || 1);
      tPnL += toUSD(pl, t.currency || 'USD');
    });
    parts.push(`TRADES: ${trades.length} open, P&L ${tPnL >= 0 ? '+' : ''}$${tPnL.toFixed(0)}`);
  }

  // Deposits, named as deposits. They left the TRADES line above; they must not leave the
  // digest, or excluding them would trade one silent error for another.
  const tds = (S.trades || []).filter(t => t && !t._del && t.status !== 'Closed' && t.type === 'TimeDeposit');
  if (tds.length) {
    const principalUSD = tds.reduce((a, t) => a + toUSD((t.td && t.td.principal) || t.entry || 0, t.currency || 'TRY'), 0);
    parts.push(`TIME DEPOSITS: ${tds.length} open, principal $${principalUSD.toFixed(0)} — rate vs inflation is the risk here, not price. Never suggest a stop-loss on these.`);
  }

  // 2. Market intelligence
  try {
    const intel = await getMarketIntel({ all: true });

    if (intel.fearGreed?.current) {
      parts.push(`FEAR & GREED: ${intel.fearGreed.current.value} (${intel.fearGreed.current.label})`);
    }

    if (intel.cryptoGlobal) {
      const cg = intel.cryptoGlobal;
      const mcap = cg.totalMarketCapUSD >= 1e12
        ? (cg.totalMarketCapUSD / 1e12).toFixed(2) + 'T'
        : (cg.totalMarketCapUSD / 1e9).toFixed(0) + 'B';
      parts.push(`CRYPTO: MarketCap $${mcap}, BTC dom ${cg.btcDominance}%, 24h ${cg.marketCapChange24h >= 0 ? '+' : ''}${cg.marketCapChange24h}%`);
    }

    if (intel.trending?.length) {
      parts.push(`TRENDING: ${intel.trending.slice(0, 5).map(c => c.symbol).join(', ')}`);
    }

    if (intel.news?.length) {
      // Headlines from the internet, pipe-joined. promptSafe or the separator is forgeable.
      parts.push(`NEWS: ${intel.news.slice(0, 4).map(n => promptSafe(n.title, 200)).join(' | ')}`);
    }

    if (intel.internal) {
      const { fx, marketBreadth, bigMovers } = intel.internal;
      if (fx.USDTRY) parts.push(`FX: USD/TRY ${fx.USDTRY.toFixed(2)}, EUR/TRY ${fx.EURTRY.toFixed(2)}`);
      if (marketBreadth.total > 0) {
        parts.push(`BREADTH: ${marketBreadth.gainers} up, ${marketBreadth.losers} down, ${marketBreadth.unchanged} flat`);
      }
      if (bigMovers?.length) {
        parts.push(`BIG MOVERS: ${bigMovers.map(m => `${m.ticker} ${m.changePct >= 0 ? '+' : ''}${m.changePct}%`).join(', ')}`);
      }
    }
  } catch (_e) {
    parts.push('MARKET DATA: partially unavailable');
  }

  return parts.join('\n');
}

// ─── LLM DIGEST GENERATION ─────────────────────────────────────

const DIGEST_SYSTEM_PROMPT = `You are SPEKTRX's daily market intelligence briefing engine. Generate a concise, actionable morning digest for a senior portfolio manager.

Rules:
- Max 200 words, structured in short paragraphs
- Use these section headers: MARKET, PORTFOLIO, TRADES (if any), ACTIONS
- Be direct, no fluff. Use numbers and percentages.
- Highlight anything that needs attention (SL proximity, big movers, sentiment shifts)
- End with 1-2 actionable suggestions based on the data
- All text in English
- Do NOT use markdown headers (#) — use plain section names in caps`;

/** Generate a fresh AI digest */
export async function generateDigest() {
  if (_digestLoading) return _loadDigest();
  if (!isAIConfigured()) return null;

  _digestLoading = true;
  _renderLoading();

  try {
    const context = await _buildDigestContext();
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });

    const messages = [
      { role: 'system', content: DIGEST_SYSTEM_PROMPT },
      { role: 'user', content: `Generate today's market digest (${today}).\n\nDATA:\n${context}` },
    ];

    const result = await chatCompletion(messages, {
      maxTokens: 500,
      temperature: 0.3,
      signal: AbortSignal.timeout(30000),
    });

    const digest = {
      text: result.content,
      ts: Date.now(),
      model: result.model || 'unknown',
    };

    _saveDigest(digest);
    _renderDigestContent(digest);
    return digest;
  } catch (_e) {
    console.warn('[AI-Digest] generation failed:', _e.message);
    _renderError(_e.message);
    return null;
  } finally {
    _digestLoading = false;
  }
}

/** Get last cached digest without generating */
export function getLastDigest() {
  return _loadDigest();
}

// ─── RENDERING ──────────────────────────────────────────────────

/** Render the digest card on dashboard — auto-generate if stale */
export async function renderDigestCard() {
  const card = document.getElementById('aiDigestCard');
  if (!card) return;

  // If AI not configured, show setup hint
  if (!isAIConfigured()) {
    card.style.display = 'block';
    card.querySelector('.digest-body').innerHTML =
      '<div style="text-align:center;padding:12px;color:var(--t3);font-size:11px">Configure AI in Settings to enable daily digest</div>';
    return;
  }

  card.style.display = 'block';

  // Check for cached digest
  const cached = _loadDigest();
  if (cached && !_isStale()) {
    _renderDigestContent(cached);
    return;
  }

  // Stale or no digest — auto-generate in background
  generateDigest();
}

function _renderLoading() {
  const body = document.getElementById('digestBody');
  if (!body) return;
  body.innerHTML = `
    <div style="text-align:center;padding:16px">
      <div style="display:inline-block;width:20px;height:20px;border:2px solid var(--acc);border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite"></div>
      <div style="font-size:11px;color:var(--t3);margin-top:8px">Generating market digest...</div>
    </div>`;
}

function _renderDigestContent(digest) {
  const body = document.getElementById('digestBody');
  const tsEl = document.getElementById('digestTs');
  if (!body) return;

  if (tsEl) {
    const d = new Date(digest.ts);
    tsEl.textContent = d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  // Parse sections and format with styled headers
  const text = (digest.text || '').trim();
  const html = text
    .split('\n')
    .map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '<div style="height:6px"></div>';
      // Detect section headers (MARKET, PORTFOLIO, TRADES, ACTIONS, etc.)
      if (/^[A-Z]{3,}[A-Z &]*:?$/.test(trimmed) || /^[A-Z]{3,}[A-Z &]*:/.test(trimmed)) {
        const colonIdx = trimmed.indexOf(':');
        if (colonIdx > 0 && colonIdx < 20) {
          const header = trimmed.slice(0, colonIdx);
          const rest = trimmed.slice(colonIdx + 1).trim();
          return `<div style="margin-top:8px"><span style="font-size:9px;font-weight:700;color:var(--acc);letter-spacing:1px">${esc(header)}</span>${rest ? ' <span style="font-size:11px;color:var(--t2)">' + esc(rest) + '</span>' : ''}</div>`;
        }
        return `<div style="margin-top:8px;font-size:9px;font-weight:700;color:var(--acc);letter-spacing:1px">${esc(trimmed)}</div>`;
      }
      return `<div style="font-size:11px;color:var(--t2);line-height:1.5">${esc(trimmed)}</div>`;
    })
    .join('');

  body.innerHTML = html;
}

function _renderError(msg) {
  const body = document.getElementById('digestBody');
  if (!body) return;
  body.innerHTML = `<div style="padding:8px;color:var(--ora);font-size:11px">Digest generation failed: ${esc(msg)}</div>`;
}
